import type { ProductsFilterType } from '@Store/types'

import type { ConditionRow, FilterBuilderState, MatchOperator } from './types'

let counter = 0
const makeId = () => `cond-${Date.now()}-${++counter}`

const toOperator = (v?: string): MatchOperator => (v === 'or' ? 'or' : 'and')

export function fromAPIParams(filters: ProductsFilterType): FilterBuilderState {
  const productOp = toOperator(filters.product_filter_operator)
  const competitorOp = toOperator(filters.competitor_filter_operator)

  const productRows: ConditionRow[] = []
  const competitorRows: ConditionRow[] = []

  if (filters.product_name)
    productRows.push({
      id: makeId(),
      attribute: 'name',
      value: filters.product_name,
      operator: productOp,
      filterOperator:
        filters.product_name_operator === 'not_contains'
          ? 'not_contains'
          : 'contains',
    })
  if (filters.category)
    productRows.push({
      id: makeId(),
      attribute: 'category',
      value: filters.category,
      operator: productOp,
    })
  if (filters.product_code)
    productRows.push({
      id: makeId(),
      attribute: 'product_code',
      value: filters.product_code,
      operator: productOp,
      filterOperator:
        filters.product_code_operator === 'not_contains'
          ? 'not_contains'
          : 'contains',
    })
  if (filters.brand)
    productRows.push({
      id: makeId(),
      attribute: 'brand',
      value: filters.brand,
      operator: productOp,
    })
  if (filters.product_barcode)
    productRows.push({
      id: makeId(),
      attribute: 'barcode',
      value: filters.product_barcode,
      operator: productOp,
      filterOperator:
        filters.product_barcode_operator === 'not_contains'
          ? 'not_contains'
          : 'contains',
    })
  if (filters.has_barcode != null)
    productRows.push({
      id: makeId(),
      attribute: 'barcode',
      value: '',
      operator: productOp,
      filterOperator: Number(filters.has_barcode) === 1 ? 'has' : 'has_not',
    })
  if (filters.tag)
    productRows.push({
      id: makeId(),
      attribute: 'tags',
      value: filters.tag,
      operator: productOp,
      filterOperator: filters.tag_filter_operator ?? undefined,
    })
  if (filters.is_cheap)
    productRows.push({
      id: makeId(),
      attribute: 'position',
      value: filters.is_cheap,
      operator: productOp,
    })
  if (filters.filter_match != null)
    productRows.push({
      id: makeId(),
      attribute: 'matching',
      value: filters.filter_match,
      operator: productOp,
    })
  // product_index intentionally skipped — backend not ready
  if (
    filters.my_price_min ||
    filters.my_price_max ||
    filters.price_min ||
    filters.price_max
  ) {
    productRows.push({
      id: makeId(),
      attribute: 'my_price',
      value: [
        (filters.my_price_min ?? filters.price_min)?.toString() ?? '',
        (filters.my_price_max ?? filters.price_max)?.toString() ?? '',
      ],
      operator: productOp,
      filterOperator:
        filters.my_price_operator === 'not_between' ? 'not_between' : 'between',
    })
  }
  if (filters.cost_min || filters.cost_max) {
    productRows.push({
      id: makeId(),
      attribute: 'my_product_cost',
      value: [
        filters.cost_min?.toString() ?? '',
        filters.cost_max?.toString() ?? '',
      ],
      operator: productOp,
    })
  }
  if (filters.total_price_min || filters.total_price_max) {
    productRows.push({
      id: makeId(),
      attribute: 'my_total_price',
      value: [
        filters.total_price_min?.toString() ?? '',
        filters.total_price_max?.toString() ?? '',
      ],
      operator: productOp,
    })
  }
  if (filters.my_change != null || filters.filter_change != null)
    productRows.push({
      id: makeId(),
      attribute: 'my_change',
      value: (filters.my_change ?? filters.filter_change) as string,
      operator: productOp,
    })
  if (filters.my_stock != null)
    productRows.push({
      id: makeId(),
      attribute: 'my_stock',
      value: filters.my_stock.toString(),
      operator: productOp,
    })
  if (
    filters.filter_favorites_only === 'on' ||
    filters.filter_favorites_only === 'off'
  )
    productRows.push({
      id: makeId(),
      attribute: 'favorites_only',
      value: filters.filter_favorites_only,
      operator: productOp,
    })
  if (filters.filter_my_url_status)
    productRows.push({
      id: makeId(),
      attribute: 'my_url_status',
      value: filters.filter_my_url_status,
      operator: productOp,
    })
  if (filters.filter_ismine != null)
    productRows.push({
      id: makeId(),
      attribute: 'my_ismine',
      value: '',
      operator: productOp,
      filterOperator: Number(filters.filter_ismine) === 1 ? 'has' : 'has_not',
    })

  if (filters.company)
    competitorRows.push({
      id: makeId(),
      attribute: 'competitor',
      value: filters.company,
      operator: competitorOp,
      filterOperator: filters.company_filter_operator ?? 'and',
    })
  if (filters.competitor_price_min || filters.competitor_price_max) {
    competitorRows.push({
      id: makeId(),
      attribute: 'competitor_price',
      value: [
        filters.competitor_price_min?.toString() ?? '',
        filters.competitor_price_max?.toString() ?? '',
      ],
      operator: competitorOp,
      filterOperator:
        filters.competitor_price_operator === 'not_between'
          ? 'not_between'
          : 'between',
    })
  }
  if (
    filters.competitor_total_price_min ||
    filters.competitor_total_price_max
  ) {
    competitorRows.push({
      id: makeId(),
      attribute: 'competitor_total_price',
      value: [
        filters.competitor_total_price_min?.toString() ?? '',
        filters.competitor_total_price_max?.toString() ?? '',
      ],
      operator: competitorOp,
    })
  }
  if (filters.competitor_change != null)
    competitorRows.push({
      id: makeId(),
      attribute: 'competitor_change',
      value: filters.competitor_change,
      operator: competitorOp,
    })
  if (filters.competitor_stock != null)
    competitorRows.push({
      id: makeId(),
      attribute: 'competitor_stock',
      value: filters.competitor_stock.toString(),
      operator: competitorOp,
    })

  return {
    productRows,
    competitorRows,
    productOperator: productOp,
    competitorOperator: competitorOp,
    sectionOperator: toOperator(filters.section_filter_operator),
  }
}

export function toAPIParams(state: FilterBuilderState): ProductsFilterType {
  const params: any = {}

  for (const row of state.productRows) {
    const v = row.value
    const fo = row.filterOperator // used for barcode has/has_not
    switch (row.attribute) {
      case 'name':
        if (v) {
          params.product_name = v
          if (fo === 'not_contains')
            params.product_name_operator = 'not_contains'
        }
        break
      case 'category':
        if (v) params.category = v
        break
      case 'product_code':
        if (v) {
          params.product_code = v
          if (fo === 'not_contains')
            params.product_code_operator = 'not_contains'
        }
        break
      case 'brand':
        if (v) params.brand = v
        break
      case 'barcode':
        if (fo === 'has') params.has_barcode = '1'
        else if (fo === 'has_not') params.has_barcode = '0'
        else if (v) {
          params.product_barcode = v
          if (fo === 'not_contains')
            params.product_barcode_operator = 'not_contains'
        }
        break
      case 'tags':
        if (v) {
          params.tag = v
          if (fo === 'and' || fo === 'or') params.tag_filter_operator = fo
        }
        break
      case 'position':
        if (v) params.is_cheap = v
        break
      case 'matching':
        if (v) params.filter_match = v
        break
      // product_index intentionally skipped — backend not ready
      case 'my_price':
        if (Array.isArray(v)) {
          if (v[0]) params.my_price_min = v[0].toString().replace(',', '.')
          if (v[1]) params.my_price_max = v[1].toString().replace(',', '.')
          if (fo === 'not_between') params.my_price_operator = 'not_between'
        }
        break
      case 'my_product_cost':
        if (Array.isArray(v)) {
          if (v[0]) params.cost_min = v[0].toString().replace(',', '.')
          if (v[1]) params.cost_max = v[1].toString().replace(',', '.')
        }
        break
      case 'my_total_price':
        if (Array.isArray(v)) {
          if (v[0]) params.total_price_min = v[0].toString().replace(',', '.')
          if (v[1]) params.total_price_max = v[1].toString().replace(',', '.')
        }
        break
      case 'my_change':
        if (v) params.my_change = v
        break
      case 'my_stock':
        if (v != null && v !== '') params.my_stock = v
        break
      case 'favorites_only':
        if (v === 'on' || v === 'off') params.filter_favorites_only = v
        break
      case 'my_url_status':
        if (v) params.filter_my_url_status = v
        break
      case 'my_ismine':
        if (fo === 'has') params.filter_ismine = '1'
        else if (fo === 'has_not') params.filter_ismine = '0'
        break
    }
  }

  for (const row of state.competitorRows) {
    const v = row.value
    const fo = row.filterOperator
    switch (row.attribute) {
      case 'competitor':
        if (v) {
          params.company = v
          if (row.filterOperator === 'and' || row.filterOperator === 'or')
            params.company_filter_operator = row.filterOperator
        }
        break
      case 'competitor_price':
        if (Array.isArray(v)) {
          if (v[0])
            params.competitor_price_min = v[0].toString().replace(',', '.')
          if (v[1])
            params.competitor_price_max = v[1].toString().replace(',', '.')
          if (fo === 'not_between')
            params.competitor_price_operator = 'not_between'
        }
        break
      case 'competitor_total_price':
        if (Array.isArray(v)) {
          if (v[0])
            params.competitor_total_price_min = v[0]
              .toString()
              .replace(',', '.')
          if (v[1])
            params.competitor_total_price_max = v[1]
              .toString()
              .replace(',', '.')
        }
        break
      case 'competitor_change':
        if (v) params.competitor_change = v
        break
      case 'competitor_stock':
        if (v != null && v !== '') params.competitor_stock = v
        break
      case 'competitor_url_status':
        if (v) params.filter_competitor_url_status = v
        break
    }
  }

  if (state.productOperator === 'or') params.product_filter_operator = 'or'
  if (state.competitorOperator === 'or')
    params.competitor_filter_operator = 'or'
  if (state.sectionOperator === 'or') params.section_filter_operator = 'or'

  return params
}

export function buildFilterUrl(state: FilterBuilderState): string {
  return JSON.stringify(toAPIParams(state))
}
